import { useGetAllRecommendedNonProfits } from '@/actions/nonprofit.actions';
import { fetchViaProxy } from '@/actions/proxy-fetch';
import Avatar from '@/components/next-ui/Avatar';
import Button from '@/components/next-ui/Button';
import Input from '@/components/next-ui/Input';
import Typography from '@/components/next-ui/Typography';
import useDebounce from '@/hooks/useDebounce';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import { getCountryName } from '@/utils/stringHelpers';
import { useQuery } from '@tanstack/react-query';
import { cx } from 'cva';
import { isEmpty, trim } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { MdCheck } from 'react-icons/md';

const searchNonProfits = async (searchText) => {
  if (isEmpty(searchText)) {
    return null;
  }
  const { ok, data } = await fetchViaProxy(
    `/proxy/v2/protected/nonprofits/search?name=${searchText}&limit=10`,
    {
      method: 'GET',
    },
  );

  if (!ok) {
    throw new Error('Something went wrong');
  }
  return data.data;
};

function AddNonProfitToPost(props) {
  const { onChange } = props;

  const [nonProfitSearchText, setNonProfitSearchText] = useState('');
  const debouncedSearchText = useDebounce(nonProfitSearchText, 500) || '';
  const [selected, setSelected] = useState(null);
  const [doRenderList, setDoRenderList] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const isLessThanSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('sm'));

  const { data: recommendedNonProfits, isLoading: isLoadingRecommended } =
    useGetAllRecommendedNonProfits();

  const { data: nonProfits, isLoading: isSearching } = useQuery({
    queryKey: [`nonprofits-for-post`, debouncedSearchText],
    queryFn: () => {
      return searchNonProfits(debouncedSearchText);
    },
  });

  useEffect(() => {
    const showList = !isLoadingRecommended && !isSearching;
    if (showList) {
      setDoRenderList(true);
    }
  }, [isLoadingRecommended, isSearching, debouncedSearchText]);

  // const doRenderList = !isLoadingRecommended && !isSearching;

  const nonProfitsToDisplay = isEmpty(trim(nonProfitSearchText))
    ? recommendedNonProfits || []
    : nonProfits || [];

  const handleSearchTextChange = (e) => {
    if (doRenderList) {
      setDoRenderList(false);
    }
    setShowDetails(false);
    setNonProfitSearchText(e.target.value);
  };

  const handleNonProfitSelect = (nonProfit) => {
    setShowDetails(selected?.id !== nonProfit.id || !showDetails);
    setSelected(nonProfit);
  };

  const renderNonProfit = (nonProfit) => {
    return (
      <div className="w-full rounded-l-md py-4 pl-1 odd:bg-white even:bg-body-background">
        <div
          className="flex w-full cursor-pointer items-start gap-4"
          role="button"
          onClick={() => handleNonProfitSelect(nonProfit)}>
          <div className="flex-shrink-0">
            <Avatar
              src={nonProfit.logo_url}
              alt={nonProfit.name}
              profile="npo"
              className="h-12 w-12 rounded-full"
            />
          </div>
          <div className="flex flex-1 flex-col">
            <Typography variant="h4" className="break-words">
              {nonProfit.name}
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              className="break-words">
              {[
                nonProfit.city,
                nonProfit.state,
                getCountryName(nonProfit.country),
              ]
                .filter((t) => !isEmpty(trim(t)))
                .join(', ')}
            </Typography>

            {selected?.id === nonProfit.id && (
              <div
                className={cx(
                  'h-max overflow-hidden transition-all duration-500 ease-in-out',
                  showDetails
                    ? 'mt-3 max-h-screen opacity-100'
                    : 'max-h-0 opacity-0',
                )}>
                <Typography variant="body1" className="text-text-secondary">
                  {nonProfit.description}
                </Typography>
              </div>
            )}
          </div>
          <div
            className={cx(
              selected?.id === nonProfit.id ? 'flex' : 'hidden',
              'items-center justify-start',
            )}>
            <MdCheck className="mr-4 h-8 w-8 text-secondary" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full w-full flex-col gap-2 overflow-hidden pr-2">
      <Typography variant="h5" component="label" htmlFor="non-profit">
        We suggest supporting one of these non-profits to help create awareness
        and raise funds
      </Typography>

      <Input
        placeholder="Or search for your choice of nonprofits"
        onChange={handleSearchTextChange}
      />

      <div
        className={cx(
          'flex w-full flex-1 flex-col overflow-y-auto transition-opacity duration-500 ease-out md:min-h-[40vh]',
          !doRenderList && 'opacity-0',
          isLessThanSmallScreen && 'max-h-[300px]',
        )}>
        {doRenderList && nonProfitsToDisplay.length === 0 && (
          <div className="flex w-full items-center justify-center py-4">
            <Typography variant="h4" color="textDisabled">
              No results found
            </Typography>
          </div>
        )}
        {selected &&
          !nonProfitsToDisplay.some((np) => np.id === selected.id) &&
          renderNonProfit(selected)}
        {nonProfitsToDisplay.map((nonProfit) => {
          return renderNonProfit(nonProfit);
        })}
      </div>
      <div className="mt-7 flex w-full shrink-0 grow-0 items-center justify-end gap-4">
        <Button
          color="primary"
          variant="outlined"
          size="md"
          onClick={() => onChange()}>
          Back
        </Button>
        <Button
          disabled={!selected}
          color="primary"
          variant="filled"
          size="md"
          onClick={() => onChange(selected)}>
          Done
        </Button>
      </div>
    </div>
  );
}

export default AddNonProfitToPost;
